/** The control deck under the slip: one step's controls at a time. */
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Radio, RadioGroup } from '@headlessui/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { Category, Entity, Favourite, ItemPurchase, Wallet } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { allowedDates, dayMonthLabel, daysBetween, nearDayKey, shortDayLabel } from '../lib/dates.ts';
import { isValidItemName } from '../lib/items.ts';
import { rs } from '../lib/money.ts';
import { UNITS, parseQuantity, type Unit } from '../lib/units.ts';
import { useHoldDrag } from '../lib/useHoldDrag.ts';
import { CategoryIcon, EntityIcon, Icon } from '../ui/icons.tsx';
import { Num, Spinner } from '../ui/parts.tsx';
import { DateSheet } from './DateSheet.tsx';
import { MAX_PHOTOS, REASONS, type Draft, type ReasonKey } from './draft.ts';
import { categoryLabel } from './labels.ts';

const buzz = () => navigator.vibrate?.(8);

function SectionLabel({ children }: { children: ReactNode }) {
  return <span className="text-sm font-semibold text-muted rtl:text-[15px]">{children}</span>;
}

/** One plain row (minimal look, owner's request, 22 Oct 2026): used for usual buys and types. */
const rowCls =
  'flex h-14 w-full items-center gap-3 rounded-xl border border-line-strong bg-paper px-3.5 text-start text-[15px] font-semibold hover:bg-ground active:bg-ground low:h-12 rtl:text-[17px]';

const choiceCls =
  'inline-flex h-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border-[1.5px] border-line-strong bg-paper px-3 text-[14px] font-bold whitespace-nowrap text-ink select-none focus:outline-none data-checked:border-ink data-checked:bg-ink data-checked:text-white data-focus:outline-3 data-focus:outline-offset-2 data-focus:outline-ink rtl:text-[17px]';

/** One-of-many chips (Headless UI RadioGroup: arrow keys move, screen readers hear "radio, 1 of 4"). */
function Choices<T extends string>(props: {
  label: string;
  value: T | null;
  onChange: (value: T) => void;
  options: { value: T; label: ReactNode }[];
  className: string;
}) {
  return (
    <RadioGroup
      value={props.value}
      onChange={(v: T | null) => v !== null && props.onChange(v)}
      aria-label={props.label}
      className={props.className}
    >
      {props.options.map((o) => (
        <Radio key={o.value} value={o.value} className={choiceCls}>
          {o.label}
        </Radio>
      ))}
    </RadioGroup>
  );
}

/* ---------------------------------------------------------------- What did you buy? */

export function WhatStep(props: {
  entity: Entity;
  favourites: Favourite[];
  categories: Category[];
  selectedCategoryId: string | null;
  onFavourite: (f: Favourite) => void;
  onCategory: (c: Category) => void;
  /** Hold and drag a tile to put the categories in the spender's own order. */
  onReorder: (categoryIds: string[]) => void;
}) {
  const { t } = useT();
  const [more, setMore] = useState(false);
  const shown = more ? props.categories : props.categories.slice(0, 8);
  const hidden = props.categories.slice(shown.length);
  const catOf = (id: string) => props.categories.find((c) => c.id === id);
  // Only the visible rows move; hidden ones keep their order after them.
  const dnd = useHoldDrag({
    ids: shown.map((c) => c.id),
    onCommit: (ids) => props.onReorder([...ids, ...hidden.map((c) => c.id)]),
  });
  const lifted = dnd.drag ? catOf(dnd.drag.id) : undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pb-2">
      {props.favourites.length > 0 && (
        <>
          <SectionLabel>{t('add.usualShort')}</SectionLabel>
          <div className="grid gap-2 @2xl:grid-cols-2">
            {props.favourites.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => {
                  buzz();
                  props.onFavourite(f);
                }}
                className={rowCls}
              >
                <CategoryIcon icon={catOf(f.categoryId)?.icon ?? null} size={20} className="shrink-0 text-muted" />
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
                <Num className="shrink-0 font-bold">{rs(f.defaultPrice)}</Num>
              </button>
            ))}
          </div>
        </>
      )}
      <span className={props.favourites.length > 0 ? 'mt-3' : ''}>
        <SectionLabel>{t('add.orType')}</SectionLabel>
      </span>
      <span id="category-move-hint" className="sr-only">
        {t('add.holdToMoveType')}
      </span>
      <div className="grid grid-cols-2 gap-2 @2xl:grid-cols-3 @4xl:grid-cols-4">
        {dnd.order.map((id) => {
          const c = catOf(id);
          if (!c) return null;
          const on = c.id === props.selectedCategoryId;
          const slot = dnd.isSlot(id);
          return (
            <button
              key={c.id}
              type="button"
              {...dnd.bind(c.id)}
              aria-pressed={on}
              aria-describedby="category-move-hint"
              onClick={() => {
                buzz();
                props.onCategory(c);
              }}
              className={`${rowCls} touch-manipulation transition-transform duration-150 select-none [-webkit-touch-callout:none] ${
                slot ? 'border-dashed bg-ground' : on ? 'border-ink bg-ground' : ''
              } ${dnd.pressed === c.id ? 'scale-[0.97]' : ''}`}
            >
              <span className={`flex min-w-0 flex-1 items-center gap-3 ${slot ? 'invisible' : ''}`}>
                <CategoryIcon icon={c.icon} size={20} className="shrink-0 text-muted" />
                <span className="min-w-0 flex-1 truncate">{categoryLabel(c, t)}</span>
              </span>
            </button>
          );
        })}
      </div>
      {props.categories.length > 8 && (
        <button
          type="button"
          onClick={() => setMore(!more)}
          className="mt-1 inline-flex h-11 items-center gap-1.5 self-start px-1 text-[15px] font-semibold text-brand hover:underline"
        >
          <Icon name={more ? 'chevronUp' : 'chevronDown'} size={16} stroke={2.5} />
          {more ? t('add.less') : t('add.moreTypes', { count: props.categories.length - 8 })}
        </button>
      )}

      {dnd.drag && lifted && (
        // The lifted row following the pointer.
        <div
          aria-hidden="true"
          className={`${rowCls} pointer-events-none fixed z-50 border-ink shadow-[0_12px_28px_rgba(19,33,31,0.25)]`}
          style={{
            width: dnd.drag.w,
            height: dnd.drag.h,
            left: dnd.drag.x - dnd.drag.offX,
            top: dnd.drag.y - dnd.drag.offY,
          }}
        >
          <CategoryIcon icon={lifted.icon} size={20} className="shrink-0 text-muted" />
          <span className="min-w-0 flex-1 truncate">{categoryLabel(lifted, t)}</span>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Which item? */

export function ItemStep(props: {
  suggestions: ItemPurchase[];
  today: string;
  onPick: (p: ItemPurchase) => void;
  onNew: (name: string) => void;
}) {
  const { t } = useT();
  const [typed, setTyped] = useState('');
  const ok = isValidItemName(typed);
  return (
    // Past items scroll on their own; the new-name field stays pinned under them, so it is always
    // in view (also with the keyboard open). Inner padding keeps the field's focus glow unclipped.
    <div className="flex min-h-0 flex-1 flex-col gap-2 pb-3">
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-1 pt-1">
        {props.suggestions.length === 0 && <p className="m-0 text-[15px] text-muted">{t('add.noHistory')}</p>}
        <div className="grid gap-2 @2xl:grid-cols-2">
          {props.suggestions.map((p) => {
            const days = daysBetween(p.txnDate, props.today);
            const ago = days === 0 ? t('ago.today') : days === 1 ? t('ago.yesterday') : t('ago.days', { count: days });
            return (
              <button
                key={p.itemName}
                type="button"
                onClick={() => {
                  buzz();
                  props.onPick(p);
                }}
                className="flex min-h-[66px] shrink-0 items-center gap-3 rounded-[14px] border-[1.5px] border-line-strong bg-paper px-3.5 py-2 text-start hover:bg-ground active:bg-ground low:min-h-14 low:py-1.5"
              >
                <span className="flex min-w-0 flex-1 flex-col leading-snug">
                  <span className="truncate text-fluid-lg font-bold">{p.itemName}</span>
                  <span className="text-sm text-muted">
                    {p.quantity ? (
                      <>
                        <Num>{p.quantity}</Num> {t(`units.${p.unit ?? 'pcs'}`)} ·{' '}
                      </>
                    ) : null}
                    <Num>{rs(p.amount)}</Num> · {ago}
                  </span>
                </span>
                <Icon name="chevronEnd" flip />
              </button>
            );
          })}
        </div>
      </div>
      <label htmlFor="new-item" className="mt-1 shrink-0 px-1 text-sm font-bold text-muted kb:sr-only rtl:text-[15px]">
        {t('add.newName')}
      </label>
      {/* One bordered field holding the input and its button: the input can never grow past the
          border, and the box lines up edge to edge with the item cards above. */}
      <form
        className="mx-1 flex h-[66px] w-[calc(100%-0.5rem)] min-w-0 shrink-0 items-center gap-1.5 rounded-[14px] low:h-14 border-[1.5px] border-line-strong bg-paper p-1.5 focus-within:border-brand focus-within:shadow-[0_0_0_3px_rgba(15,110,106,0.2)]"
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) props.onNew(typed.trim());
        }}
      >
        <input
          id="new-item"
          value={typed}
          maxLength={60}
          size={1}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={t('add.newPh')}
          autoComplete="off"
          enterKeyHint="next"
          className="h-full w-full min-w-0 flex-1 bg-transparent px-2 text-[clamp(16px,4.4vw,17px)] outline-none"
        />
        <button
          type="submit"
          disabled={!ok}
          className="h-12 shrink-0 rounded-[10px] bg-brand px-4 text-base font-bold text-white hover:bg-brand-dark disabled:bg-line disabled:text-muted low:h-[42px]"
        >
          {t('add.use')}
        </button>
      </form>
    </div>
  );
}

/* ---------------------------------------------------------------- How much? */

/** Digits only (Urdu and Arabic digits too), no leading zeros, at most 6 (Rs 999,999). */
function amountDigits(typed: string): string {
  let out = '';
  for (const raw of typed) {
    const urdu = '۰۱۲۳۴۵۶۷۸۹'.indexOf(raw);
    const arabic = '٠١٢٣٤٥٦٧٨٩'.indexOf(raw);
    const ch = urdu >= 0 ? String(urdu) : arabic >= 0 ? String(arabic) : raw;
    if (ch >= '0' && ch <= '9') out += ch;
  }
  return out.replace(/^0+/, '').slice(0, 6);
}

export function AmountStep(props: {
  draft: Draft;
  over: boolean;
  canNext: boolean;
  /** What is still missing (shown above Next), or null. */
  hint: string | null;
  lastPrice: number | null;
  onAmount: (digits: string) => void;
  onQuantity: (dir: 1 | -1) => void;
  /** A quantity typed by hand (0 = none). */
  onQuantityTyped: (value: number) => void;
  onUnit: (u: Unit) => void;
  onNext: () => void;
}) {
  const { t } = useT();
  const d = props.draft;
  // What the quantity field shows: follows − / + and the draft, except while being typed in
  // (so "1." isn't turned back into "1" mid-typing).
  const [qtyText, setQtyText] = useState(d.quantity ? String(d.quantity) : '');
  const editing = useRef(false);
  useEffect(() => {
    if (!editing.current) setQtyText(d.quantity ? String(d.quantity) : '');
  }, [d.quantity]);
  const field =
    'h-14 rounded-xl border border-line-strong bg-paper focus-within:border-ink focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-ink low:h-12';
  const stepBtn =
    'flex h-14 w-12 shrink-0 items-center justify-center rounded-xl border border-line-strong bg-paper hover:bg-ground low:h-12 min-[400px]:w-14';

  return (
    // Two plain fields (owner's request, 22 Oct 2026): quantity with its unit, and the amount
    // paid; both start empty and are required. Next stays pinned at the bottom.
    <form
      className="flex min-h-0 flex-1 flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (props.canNext) props.onNext();
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[520px] flex-col gap-2 p-0.5">
          <label htmlFor="quantity" className="text-sm font-semibold text-muted rtl:text-[15px]">
            {t('add.quantity')}
          </label>
          {/* Phone keypads are not mirrored in Urdu, so neither is this row (− number +). */}
          <div dir="ltr" className="flex items-center gap-2">
            <button type="button" aria-label="−" onClick={() => props.onQuantity(-1)} className={stepBtn}>
              <Icon name="minus" size={20} stroke={2.5} />
            </button>
            <input
              id="quantity"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              enterKeyHint="next"
              aria-required="true"
              value={qtyText}
              placeholder={t('add.qtyPh')}
              onFocus={(e) => {
                editing.current = true;
                e.target.select();
              }}
              onBlur={() => {
                editing.current = false;
                setQtyText(d.quantity ? String(d.quantity) : '');
              }}
              onChange={(e) => {
                const { text, value } = parseQuantity(e.target.value);
                setQtyText(text);
                props.onQuantityTyped(value);
              }}
              className={`${field} num min-w-0 flex-1 px-3 text-center text-xl font-bold outline-none placeholder:text-base placeholder:font-normal placeholder:text-muted`}
            />
            <button type="button" aria-label="+" onClick={() => props.onQuantity(1)} className={stepBtn}>
              <Icon name="plus" size={20} stroke={2.5} />
            </button>
            {/* The unit, in the same row: a small dropdown (empty until picked; required). */}
            <Listbox value={d.unit} onChange={(u: Unit | null) => u && props.onUnit(u)}>
              <ListboxButton
                aria-label={t('add.unit')}
                className={`flex h-14 min-w-[92px] shrink-0 items-center justify-between gap-1.5 rounded-xl border bg-paper px-3 text-[16px] font-semibold focus:outline-none data-focus:outline-2 data-focus:outline-offset-1 data-focus:outline-ink data-open:border-ink low:h-12 rtl:text-[17px] ${
                  d.unit ? 'border-line-strong text-ink' : 'border-line-strong text-muted'
                }`}
              >
                <span>{d.unit ? t(`units.${d.unit}`) : t('add.unit')}</span>
                <Icon name="chevronDown" size={14} stroke={2.5} />
              </ListboxButton>
              <ListboxOptions
                anchor="bottom end"
                transition
                className="z-50 w-44 rounded-xl border border-line-strong bg-paper p-1 shadow-[0_10px_28px_rgba(19,33,31,0.18)] [--anchor-gap:6px] [--anchor-padding:12px] transition duration-100 ease-out focus:outline-none data-closed:opacity-0"
              >
                {UNITS.map((u) => (
                  <ListboxOption
                    key={u}
                    value={u}
                    className="flex h-11 cursor-pointer items-center justify-between rounded-lg px-3 text-[15px] font-semibold select-none data-focus:bg-ground rtl:text-[17px]"
                  >
                    {t(`units.${u}`)}
                    {d.unit === u && <Icon name="check" size={16} stroke={2.75} />}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Listbox>
          </div>

          <span className="mt-4 flex items-center justify-between gap-2">
            <label htmlFor="amount" className="text-sm font-semibold text-muted rtl:text-[15px]">
              {t('add.amountPaid')}
            </label>
            {props.lastPrice !== null && String(props.lastPrice) !== d.amount && (
              <button
                type="button"
                onClick={() => {
                  buzz();
                  props.onAmount(String(props.lastPrice));
                }}
                className="inline-flex h-9 min-w-0 items-center gap-1 text-sm font-semibold text-brand hover:underline"
              >
                <span className="truncate">{t('add.sameAsLast')}</span> · <Num>{rs(props.lastPrice)}</Num>
              </button>
            )}
          </span>
          <div dir="ltr" className={`${field} flex items-center gap-2 px-4 ${props.over ? 'border-danger' : ''}`}>
            <span className="text-lg font-semibold text-muted">Rs</span>
            <input
              id="amount"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              enterKeyHint="next"
              aria-required="true"
              value={d.amount}
              placeholder={t('add.amountPh')}
              onChange={(e) => props.onAmount(amountDigits(e.target.value))}
              className="num h-full min-w-0 flex-1 bg-transparent text-xl font-bold outline-none placeholder:text-base placeholder:font-normal placeholder:text-muted"
            />
          </div>
          {props.over && <span className="text-sm font-semibold text-danger-text">{t('add.notEnough')}</span>}
        </div>
      </div>

      <div className="sticky bottom-0 mx-auto flex w-full max-w-[520px] shrink-0 flex-col gap-1.5 bg-ground pt-1 pb-1 split:bg-paper">
        {props.hint && !props.over && <span className="text-center text-sm text-muted rtl:text-[15px]">{props.hint}</span>}
        <button
          type="submit"
          disabled={!props.canNext}
          className="flex h-14 min-w-0 items-center justify-center gap-2 rounded-xl bg-brand text-lg font-bold text-white hover:bg-brand-dark disabled:bg-line disabled:text-muted low:h-12 rtl:text-[19px]"
        >
          <span className="truncate">{props.over ? t('add.notEnough') : t('add.next')}</span>
          {!props.over && <Icon name="arrowEnd" size={20} stroke={2.5} flip />}
        </button>
      </div>
    </form>
  );
}

/* ---------------------------------------------------------------- Receipt photo + date */

export function ProofStep(props: {
  draft: Draft;
  today: string;
  preparing: boolean;
  saving: boolean;
  canSave: boolean;
  hint: string | null;
  onTakePhoto: () => void;
  /** A photo already on the phone (a receipt photographed earlier, then thrown away). */
  onPickPhoto: () => void;
  onRemovePhoto: (id: string) => void;
  onNoReceipt: (on: boolean) => void;
  onReason: (r: ReasonKey) => void;
  onReasonText: (s: string) => void;
  onDate: (iso: string) => void;
  onSave: () => void;
  /** His entries already saved on the picked date (same wallet), so he doesn't add one twice. */
  sameDay?: { count: number; total: number } | null;
}) {
  const { t, lang } = useT();
  const d = props.draft;
  const showCamera = d.photos.length === 0 && !d.noReceipt;
  const reasonLabel: Record<ReasonKey, string> = {
    shop: t('add.reasonShop'),
    street: t('add.reasonStreet'),
    lost: t('add.reasonLost'),
    other: t('add.reasonOther'),
  };
  const days = allowedDates(props.today);
  const [calendar, setCalendar] = useState(false);
  const dateRow = useRef<HTMLElement>(null);
  const inRow = days.includes(d.txnDate);
  const pickedNear = nearDayKey(d.txnDate, props.today);
  useEffect(() => {
    // Start with the picked day (usually Today, in the middle of the row) in view; an older day
    // picked from the calendar sits first in the row.
    dateRow.current?.querySelector(`[data-iso="${d.txnDate}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center' });
    // When the step opens, and when the calendar adds or removes the older-day chip.
  }, [inRow]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2.5 low:gap-2">
      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-x-hidden overflow-y-auto low:gap-1.5">
        {showCamera && (
          // The receipt card fills the free space (owner's request, 30 Oct 2026): what to do, a
          // tip, and the two ways to add it; it shrinks on short phones.
          <div className="flex min-h-[248px] flex-1 flex-col items-center justify-center gap-3.5 rounded-2xl border-2 border-dashed border-line-strong bg-paper px-4 py-5 text-center low:min-h-0 low:gap-2.5 low:py-3">
            <span className="flex size-16 items-center justify-center rounded-full bg-brand-tint text-brand-dark low:size-12 tight:hidden">
              <Icon name="receipt" size={30} stroke={1.9} />
            </span>
            <span className="flex max-w-xs flex-col gap-1">
              <strong className="text-[17px] rtl:text-[19px]">{t('add.receiptTitle')}</strong>
              <span className="text-[13px] leading-snug text-muted rtl:text-[15px] tight:hidden">{t('add.receiptHint')}</span>
            </span>
            <div className="grid w-full max-w-sm grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={props.onTakePhoto}
                disabled={props.preparing}
                className="flex h-20 flex-col items-center justify-center gap-1.5 rounded-xl bg-brand px-2 text-[15px] font-bold whitespace-nowrap text-white hover:bg-brand-dark active:opacity-90 disabled:opacity-70 low:h-[72px] tight:h-12 tight:flex-row short:h-12 short:flex-row rtl:text-[17px]"
              >
                {props.preparing ? <Spinner size={24} /> : <Icon name="camera" size={26} stroke={1.9} className="tight:size-5 short:size-5" />}
                <span className="leading-tight">{props.preparing ? t('add.preparing') : t('add.takePhoto')}</span>
              </button>
              <button
                type="button"
                onClick={props.onPickPhoto}
                disabled={props.preparing}
                className="flex h-20 flex-col items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-line-strong bg-paper px-2 text-[15px] font-bold whitespace-nowrap text-ink hover:bg-ground active:bg-ground disabled:opacity-70 low:h-[72px] tight:h-12 tight:flex-row short:h-12 short:flex-row rtl:text-[17px]"
              >
                <Icon name="image" size={26} stroke={1.9} className="text-brand tight:size-5 short:size-5" />
                <span className="leading-tight">{t('add.fromGallery')}</span>
              </button>
            </div>
            <button
              type="button"
              onClick={() => props.onNoReceipt(true)}
              className="h-9 px-3 text-[14px] font-semibold text-muted underline underline-offset-4 hover:text-ink rtl:text-[16px]"
            >
              {t('add.noReceipt')}
            </button>
          </div>
        )}

        {d.photos.length > 0 && (
          <>
            {d.photos.length < MAX_PHOTOS && (
              <span className="text-[13px] font-semibold text-muted rtl:text-[15px]">{t('add.addAnother')}</span>
            )}
            <div className="flex shrink-0 gap-2.5 pt-2">
              {d.photos.map((p) => (
                <div key={p.id} className="relative h-[100px] w-[82px] rounded-[10px] border-[1.5px] border-line-strong bg-ground">
                  <img src={p.url} alt="" className="size-full rounded-[9px] object-cover" />
                  <button
                    type="button"
                    aria-label={t('add.removePhoto')}
                    onClick={() => props.onRemovePhoto(p.id)}
                    className="absolute -top-3.5 -end-3.5 flex size-11 items-center justify-center"
                  >
                    <span className="flex size-[26px] items-center justify-center rounded-full bg-ink text-white">
                      <Icon name="close" size={13} stroke={3.5} />
                    </span>
                  </button>
                </div>
              ))}
              {d.photos.length < MAX_PHOTOS && (
                <button
                  type="button"
                  aria-label={t('add.addFromGallery')}
                  onClick={props.onPickPhoto}
                  disabled={props.preparing}
                  className="flex h-[100px] w-[82px] flex-col items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed border-ink bg-paper px-1 text-center text-[12px] leading-tight font-bold rtl:text-[13px]"
                >
                  <Icon name="image" size={22} className="text-brand" />
                  {t('add.fromGallery')}
                </button>
              )}
              {d.photos.length < MAX_PHOTOS && (
                <button
                  type="button"
                  aria-label={t('add.addPhoto')}
                  onClick={props.onTakePhoto}
                  disabled={props.preparing}
                  className="flex h-[100px] w-[82px] flex-col items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed border-ink bg-paper px-1 text-center text-[12px] leading-tight font-bold rtl:text-[13px]"
                >
                  <Icon name="camera" size={22} />
                  {t('add.takePhoto')}
                </button>
              )}
            </div>
            <span className="flex items-center gap-2 text-[15px] font-bold text-success-text">
              {props.preparing ? <Spinner size={18} /> : <Icon name="check" size={18} stroke={2.5} />}
              {props.preparing ? t('add.preparing') : t('add.photoAdded')}
            </span>
          </>
        )}

        {d.noReceipt && d.photos.length === 0 && (
          <div className="flex shrink-0 flex-col gap-2.5 rounded-[14px] border-2 border-warning bg-paper p-3">
            <div className="flex items-center gap-2">
              <span className="flex-1 text-[17px] font-bold rtl:text-[19px]">{t('add.whyNoReceipt')}</span>
              <button
                type="button"
                onClick={() => props.onNoReceipt(false)}
                className="inline-flex h-10 items-center gap-1.5 px-2.5 text-sm font-bold"
              >
                <Icon name="camera" size={16} />
                {t('add.backToPhoto')}
              </button>
            </div>
            <Choices<ReasonKey>
              label={t('add.whyNoReceipt')}
              value={d.reason}
              onChange={props.onReason}
              options={REASONS.map((r) => ({ value: r, label: reasonLabel[r] }))}
              className="flex flex-wrap gap-2"
            />
            {d.reason === 'other' && (
              <input
                value={d.reasonText}
                onChange={(e) => props.onReasonText(e.target.value)}
                placeholder={t('add.otherPh')}
                aria-label={t('add.whyNoReceipt')}
                maxLength={200}
                className="h-[52px] rounded-xl border-[1.5px] border-line-strong bg-paper px-3.5 text-base"
              />
            )}
            <span className="flex items-center gap-2 text-sm font-semibold text-warning-text">
              <Icon name="alert" size={16} />
              {t('add.ownerWillCheck')}
            </span>
          </div>
        )}

        {/* While a reason is typed (keyboard open) the date steps aside, so the field and Save stay in view. */}
        <span className="mt-1 kb:hidden">
          <SectionLabel>{t('add.when')}</SectionLabel>
        </span>
        {/* A week back … Today … a week ahead, in date order; opens with the picked day centred.
            Days ahead have a dashed border so they aren't picked by mistake. Any older day comes
            from the calendar button after the row, and then shows as its own chip first. */}
        <div className="-m-1 flex shrink-0 items-center gap-2 p-1 kb:hidden">
          <RadioGroup
            ref={dateRow}
            value={d.txnDate}
            onChange={props.onDate}
            aria-label={t('add.when')}
            className="-m-1 flex min-w-0 flex-1 gap-2 overflow-x-auto p-1"
          >
            {!days.includes(d.txnDate) && (
              <Radio value={d.txnDate} data-iso={d.txnDate} className={`${choiceCls} gap-1.5`}>
                <Icon name="calendar" size={16} />
                {dayMonthLabel(d.txnDate, props.today, lang)}
              </Radio>
            )}
            {days.map((iso) => {
              const near = nearDayKey(iso, props.today);
              const ahead = iso > props.today;
              return (
                <Radio
                  key={iso}
                  value={iso}
                  data-iso={iso}
                  className={`${choiceCls} ${ahead ? 'border-dashed' : ''} ${iso === props.today ? 'not-data-checked:border-ink' : ''}`}
                >
                  {near ? t(near) : shortDayLabel(iso, lang)}
                </Radio>
              );
            })}
          </RadioGroup>
          <button
            type="button"
            onClick={() => setCalendar(true)}
            aria-label={t('add.pickDate')}
            title={t('add.pickDate')}
            className="flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-ink bg-paper px-3 text-[14px] font-bold hover:bg-ground focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            <Icon name="calendar" size={20} />
            <span className="hidden @sm:inline">{t('add.otherDate')}</span>
          </button>
        </div>
        {props.sameDay && props.sameDay.count > 0 && (
          <span className="flex shrink-0 items-center gap-2 text-[13px] font-semibold text-muted kb:hidden rtl:text-[15px]">
            <Icon name="info" size={16} />
            <span>
              {t('add.sameDay', {
                count: props.sameDay.count,
                date: pickedNear ? t(pickedNear) : dayMonthLabel(d.txnDate, props.today, lang),
                amount: rs(props.sameDay.total),
              })}
            </span>
          </span>
        )}
      </div>
      <DateSheet
        open={calendar}
        value={d.txnDate}
        today={props.today}
        onClose={() => setCalendar(false)}
        onPick={(iso) => {
          setCalendar(false);
          props.onDate(iso);
        }}
      />

      {props.hint && !props.canSave && (
        <span className="flex shrink-0 items-center justify-center gap-2 text-sm font-bold text-[#374151]">
          <Icon name="info" size={16} />
          {props.hint}
        </span>
      )}
      <button
        type="button"
        disabled={!props.canSave || props.saving}
        onClick={props.onSave}
        className="flex h-14 shrink-0 items-center justify-center gap-2.5 rounded-xl bg-success text-[17px] font-bold text-white hover:brightness-110 disabled:bg-line disabled:text-muted disabled:hover:brightness-100 short:h-12 rtl:text-[19px]"
      >
        {props.saving ? <Spinner size={22} /> : <Icon name="check" size={22} stroke={2.75} />}
        {props.saving ? t('add.saving') : t('add.save')} · <Num>{rs(Number(d.amount || 0))}</Num>
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------- Whose money? */

export function WalletStep(props: { entities: Entity[]; wallets: Wallet[]; currentId: string; onPick: (id: string) => void }) {
  const { t } = useT();
  return (
    <RadioGroup
      value={props.currentId}
      onChange={props.onPick}
      aria-label={t('add.whoseMoney')}
      className="grid min-h-0 flex-1 content-start gap-2.5 overflow-y-auto p-1 @lg:grid-cols-2"
    >
      {props.entities.map((e) => {
        const w = props.wallets.find((x) => x.entityId === e.id);
        const on = e.id === props.currentId;
        const low = w ? w.balance < w.lowBalanceThreshold : false;
        return (
          <Radio
            key={e.id}
            value={e.id}
            className={`flex min-h-[76px] shrink-0 cursor-pointer items-center gap-3 rounded-[14px] border-[3px] px-3.5 py-2 text-start text-white focus:outline-none data-focus:outline-3 data-focus:outline-offset-2 data-focus:outline-ink ${on ? 'border-ink' : 'border-transparent'}`}
            style={{ background: e.colour }}
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/20">
              <EntityIcon entity={e} size={22} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
              <span className="text-[17px] leading-snug font-bold rtl:text-[19px]">{e.name}</span>
              {e.ownerName && <span className="text-[13px] font-semibold text-white/90">{e.ownerName}</span>}
              {low && (
                <span className="inline-flex h-6 items-center rounded-md bg-paper px-2 text-[13px] font-bold text-danger">
                  {t('home.low')}
                </span>
              )}
            </span>
            <Num className="text-[22px] font-bold">{rs(w?.balance ?? 0)}</Num>
            {on && (
              <span className="flex size-7 items-center justify-center rounded-full bg-paper" style={{ color: e.colour }}>
                <Icon name="check" size={16} stroke={3.5} />
              </span>
            )}
          </Radio>
        );
      })}
    </RadioGroup>
  );
}

/* ---------------------------------------------------------------- Duplicate / discard */

export function Confirm(props: {
  tone: 'warning' | 'danger';
  /** Shown here on tablets and desktops (phones keep it in the deck's title row). */
  title: string;
  body: string;
  primary: string;
  secondary: string;
  onPrimary: () => void;
  onSecondary: () => void;
}) {
  return (
    // Phones: full width under the title row. Tablets and desktops: centred in the deck.
    <div className="mx-auto flex w-full max-w-lg flex-col gap-3 overflow-y-auto split:my-auto">
      <h2 className="m-0 hidden text-center text-[21px] leading-snug font-bold split:block rtl:text-[23px]">{props.title}</h2>
      <div
        className={`flex gap-3 rounded-[14px] border-2 p-4 text-[15px] leading-relaxed font-semibold rtl:text-[17px] ${
          props.tone === 'warning' ? 'border-warning bg-warning-tint text-warning-text' : 'border-line-strong bg-paper text-[#374151]'
        }`}
      >
        <Icon name="alert" size={24} />
        <span>{props.body}</span>
      </div>
      <button type="button" onClick={props.onPrimary} className="h-[60px] rounded-[14px] bg-ink text-lg font-bold text-white">
        {props.primary}
      </button>
      <button
        type="button"
        onClick={props.onSecondary}
        className={`h-14 rounded-[14px] border-[1.5px] bg-paper text-[17px] font-bold ${
          props.tone === 'danger' ? 'border-danger text-danger' : 'border-line-strong text-ink'
        }`}
      >
        {props.secondary}
      </button>
    </div>
  );
}
